/**
 * Digital Twin Types
 */

export type FileCategory = 'BIM' | 'CAD' | 'GIS' | 'PDF' | 'IMAGE' | 'OTHER';

export interface DesignFile {
  id: string;
  name: string;
  type: FileCategory;
  url: string;
  size?: number;
  description?: string;
  metadata?: Record<string, any>;
  // Conversion properties for CAD files
  drawingId?: string | number;
  designId?: string | number;
  convertedUrl?: string;
  conversionFormat?: string;
  conversionStats?: {
    vertices?: number;
    faces?: number;
    edges?: number;
  };
}

export interface ViewerProps {
  files: DesignFile[];
  selectedFileId?: string;
  onFileSelect?: (file: DesignFile) => void;
  onElementSelect?: (element: any) => void;
  height?: string | number;
  showToolbar?: boolean;
  showFileList?: boolean;
  mode?: 'view' | 'edit';
}

// BIM/CAD specific types
export interface BIMElement {
  id: string;
  globalId?: string;
  name: string;
  type: string;
  geometry?: any;
  properties?: Record<string, any>;
  quantities?: Quantity[];
  materials?: Material[];
}

export interface Quantity {
  name: string;
  value: number;
  unit?: string;
}

export interface Material {
  name: string;
  color?: string;
  properties?: Record<string, any>;
}

// GIS specific types
export interface GISFeature {
  id: string;
  name?: string;
  type: 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon';
  coordinates: number[] | number[][] | number[][][];
  properties?: Record<string, any>;
  style?: GISStyle;
}

export interface GISStyle {
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeWidth?: number;
  strokeOpacity?: number;
  icon?: string;
  iconSize?: [number, number];
}

export interface GISLayer {
  id: string;
  name: string;
  visible: boolean;
  features: GISFeature[];
  style?: GISStyle;
}

// Viewer state
export interface ViewerState {
  zoom: number;
  center?: [number, number] | [number, number, number];
  rotation?: [number, number, number];
  selectedElement?: string;
  visibleLayers: string[];
  measureMode?: 'distance' | 'area' | 'angle' | null;
}

// File type detection
export const FILE_EXTENSIONS: Record<FileCategory, string[]> = {
  BIM: ['.ifc', '.rvt', '.nwd', '.nwc', '.nwf'],
  CAD: ['.dwg', '.dxf', '.dwf', '.dws', '.dgn'],
  GIS: ['.kml', '.kmz', '.shp', '.shx', '.dbf', '.prj', '.geojson', '.gpx', '.gml'],
  PDF: ['.pdf'],
  IMAGE: ['.jpg', '.jpeg', '.png', '.gif', '.tiff', '.bmp', '.svg'],
  OTHER: ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.rar', '.gltf', '.glb', '.obj', '.fbx']
};

export function getFileCategory(filename: string): FileCategory {
  const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  for (const [category, extensions] of Object.entries(FILE_EXTENSIONS)) {
    if (extensions.includes(ext)) {
      return category as FileCategory;
    }
  }
  return 'OTHER';
}

export function isViewableIn3D(filename: string): boolean {
  const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  const extensions3D = ['.ifc', '.gltf', '.glb', '.obj', '.fbx', '.dxf'];
  return extensions3D.includes(ext);
}

export function isGISFile(filename: string): boolean {
  const category = getFileCategory(filename);
  return category === 'GIS';
}
